import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import {
  Search, Heart, ShoppingCart, User, ChevronLeft, Menu, ChevronDown,
  ThumbsUp, ThumbsDown, MoreVertical, Star
} from "lucide-react";
import { generateReviews } from "@/pages/product/ProductPage";
import { getCartCount } from "@/utils/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Electronics","Fashion","Home & Living","Sports","Beauty","Toys & Kids","Automotive","Books","Grocery","Health"];
const SORT_TABS = ["Most Helpful","Latest","Positive","Negative"];

const BARS = [
  { stars: 5, pct: 65 },
  { stars: 4, pct: 20 },
  { stars: 3, pct: 8  },
  { stars: 2, pct: 4  },
  { stars: 1, pct: 3  },
];

function StarBadge({ stars }) {
  const bg = stars >= 4 ? "#388e3c" : stars === 3 ? "#ff9f00" : "#ff6161";
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-white text-xs font-bold" style={{ background: bg }}>
      {stars} ★
    </span>
  );
}

export default function ReviewsPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSort, setActiveSort] = useState("Most Helpful");
  const [cartCount, setCartCount] = useState(() => getCartCount());

  useEffect(() => {
    const sync = () => setCartCount(getCartCount());
    window.addEventListener("it_cart_updated", sync);
    return () => window.removeEventListener("it_cart_updated", sync);
  }, []);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/products/public/${slug}`)
      .then(r => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center text-slate-400">Product not found.</div>;

  const rating  = product.star_rating || 4.5;
  const total   = product.num_reviews  || 0;
  const label   = rating >= 4.5 ? "Excellent" : rating >= 4 ? "Very Good" : rating >= 3.5 ? "Good" : "Average";
  const labelBg = rating >= 4 ? "#388e3c" : rating >= 3.5 ? "#ff9f00" : "#ff6161";

  const allPhotos = [
    ...(product.main_image ? [product.main_image] : []),
    ...(product.gallery_images || []).filter(Boolean),
    ...(product.variant_combinations || []).map(v => v.image).filter(Boolean),
  ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 12);

  // Generate more reviews for full page
  let reviews = generateReviews(product, 30);

  // Sort
  if (activeSort === "Latest")   reviews = [...reviews].sort((a, b) => b.id - a.id);
  if (activeSort === "Positive") reviews = [...reviews].sort((a, b) => b.stars - a.stars);
  if (activeSort === "Negative") reviews = [...reviews].sort((a, b) => a.stars - b.stars);

  return (
    <div className="min-h-screen bg-[#f1f3f6]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex-shrink-0">
              <div className="flex flex-col leading-tight">
                <BrandLogo size="md" />
                <span className="text-xs text-slate-400 -mt-0.5 hidden sm:block">Products for a Better Everyday</span>
              </div>
            </Link>
            <div className="flex-1 max-w-2xl">
              <div className="flex">
                <input type="text" placeholder="Search for products…" className="flex-1 px-4 py-2.5 border border-r-0 border-slate-300 rounded-l-lg text-sm focus:outline-none focus:border-orange-500" />
                <button className="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded-r-lg transition-colors"><Search className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors hidden sm:flex"><User className="w-5 h-5" /><span className="text-xs mt-0.5">Account</span></button>
              <button className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors hidden sm:flex"><Heart className="w-5 h-5" /><span className="text-xs mt-0.5">Wishlist</span></button>
              <button className="flex flex-col items-center text-slate-600 hover:text-orange-500 transition-colors relative" onClick={() => navigate("/cart")}>
                <ShoppingCart className="w-5 h-5" />
                <span className="text-xs mt-0.5">Cart</span>
                {cartCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{cartCount}</span>}
              </button>
            </div>
          </div>
        </div>
        <div className="bg-slate-800">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center overflow-x-auto no-scrollbar">
              <button className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2.5 text-sm font-medium flex-shrink-0"><Menu className="w-4 h-4" />All Categories<ChevronDown className="w-3 h-3" /></button>
              {CATEGORIES.map(cat => <button key={cat} className="text-slate-300 hover:text-white px-3 py-2.5 text-sm whitespace-nowrap hover:bg-slate-700 transition-colors flex-shrink-0">{cat}</button>)}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-5">

        {/* Back breadcrumb */}
        <button onClick={() => navigate(`/products/${slug}`)} className="flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to product
        </button>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">

          {/* ── Rating summary ─────────────────────────────────── */}
          <div className="px-5 py-5 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              {/* Left: big rating */}
              <div className="flex flex-col items-center sm:items-start sm:w-36 flex-shrink-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-5xl font-black text-slate-900">{rating.toFixed(1)}</span>
                  <span className="text-3xl text-orange-400 mt-1">★</span>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-white text-sm font-bold mb-1" style={{ background: labelBg }}>
                  {label}
                </span>
                <p className="text-xs text-slate-500 text-center sm:text-left">{total.toLocaleString()} ratings &amp; {Math.round(total * 0.3).toLocaleString()} reviews</p>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px bg-slate-200 self-stretch" />

              {/* Right: bars */}
              <div className="flex-1 space-y-2">
                {BARS.map(b => {
                  const count = Math.round(total * b.pct / 100);
                  return (
                    <div key={b.stars} className="flex items-center gap-2">
                      <StarBadge stars={b.stars} />
                      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: b.stars >= 4 ? "#388e3c" : b.stars === 3 ? "#ff9f00" : "#ff6161" }} />
                      </div>
                      <span className="text-xs text-slate-500 w-10 text-right">{count.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Photo gallery ────────────────────────────────────── */}
          {allPhotos.length > 0 && (
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {allPhotos.map((img, i) => (
                  <div key={i} className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                    <img src={img} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = "none"; }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Sort tabs ────────────────────────────────────────── */}
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="text-xs text-slate-500 mb-2 font-medium">User reviews sorted by</p>
            <div className="flex gap-2 flex-wrap">
              {SORT_TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveSort(tab)}
                  className={`px-3.5 py-1.5 rounded-full border text-xs font-semibold transition-all ${
                    activeSort === tab
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-slate-300 text-slate-600 hover:border-slate-400 bg-white"
                  }`}
                  data-testid={`sort-tab-${tab.toLowerCase().replace(/ /g,"-")}`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* ── Review list ──────────────────────────────────────── */}
          <div className="divide-y divide-slate-100">
            {reviews.map(rv => (
              <div key={rv.id} className="px-5 py-5" data-testid={`review-item-${rv.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Stars + title */}
                    <div className="flex items-center gap-2 mb-1">
                      <StarBadge stars={rv.stars} />
                      <span className="text-sm font-bold text-slate-900">{rv.title}</span>
                    </div>

                    {/* Variant info */}
                    {rv.variantInfo && (
                      <p className="text-xs text-slate-500 mb-1.5">
                        Review for: <span className="text-slate-700 font-medium">{rv.variantInfo}</span>
                      </p>
                    )}

                    {/* Body */}
                    <p className="text-sm text-slate-700 leading-relaxed mb-2">{rv.body}</p>

                    {/* Review images */}
                    {rv.images.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {rv.images.map((img, i) => (
                          <div key={i} className="w-14 h-14 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0">
                            <img src={img} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display = "none"; }} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Name, city */}
                    <p className="text-xs font-semibold text-slate-700 mb-0.5">{rv.name}, {rv.city}</p>

                    {/* Helpful row */}
                    <div className="flex items-center gap-4 mb-1.5">
                      <button className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors">
                        <ThumbsUp className="w-3.5 h-3.5" />
                        Helpful for {rv.helpful}
                      </button>
                      <button className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors">
                        <ThumbsDown className="w-3.5 h-3.5" /> {rv.thumbsDown}
                      </button>
                    </div>

                    {/* Verified + date */}
                    {rv.verified && (
                      <p className="text-xs text-green-700 font-semibold">
                        ✓ Certified Buyer · {rv.monthYear}
                      </p>
                    )}
                  </div>

                  {/* Three-dot menu */}
                  <button className="flex-shrink-0 p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-400">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
